import React, { useContext, useEffect, useState } from 'react';
import { FaCircleInfo } from 'react-icons/fa6';
import { Button, Modal } from 'rizzui';
import { toast } from 'sonner';
import useLeadStates from '../../../hooks/api-calls/leads/useLeadStates';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { ContractService } from '../../../services/contract/contractService';
import { Contract } from '../../../type/entities/contract-type';
import { Lead } from '../../../type/entities/leads-type';
import ContractForm from '../../contracts/ContractForm';
import { ContractContext } from '../../contracts/providers/ContractProvider';

interface ConvertToContractModalProps {
    isOpen: boolean;
    onClose: () => void;
    lead: Lead | null;
    onSuccess?: () => void;
}

const ConvertToContractModal: React.FC<ConvertToContractModalProps> = ({
    isOpen,
    onClose,
    lead,
    onSuccess
}) => {

    const { showClientAsideMenuBtn, isOpenClientHistory, setIsOpenClientHistory } = useContext(ContractContext);
    const { handleErrors } = useHandleErrors();
    const { getLeadStatesList } = useLeadStates();

    const [isLoading, setIsLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [originalContractData, setOriginalContractData] = useState<Contract | null>(null);
    const [isLoadingContractData, setIsLoadingContractData] = useState(false);

    // Reset validation errors when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setValidationErrors([]);
        }
    }, [isOpen]);

    // Fetch original contract data when modal opens and originalContractId is available
    useEffect(() => {
        const fetchOriginalContractData = async () => {
            if (!isOpen || !lead?.originalContractId) {
                setOriginalContractData(null);
                return;
            }

            setIsLoadingContractData(true);
            try {
                const contractService = new ContractService();
                const response = await contractService.getContractById(lead.originalContractId);
                const contractData = response.getResponseData();

                if (contractData && contractData?.data) {
                    setOriginalContractData(contractData.data);
                } else {
                    toast.error('No se ha podido cargar el contrato original');
                }
            } catch (error) {
                console.error('Error fetching original contract data:', error);
                toast.error('Error al cargar los datos del contrato original');
                handleErrors(error);
            } finally {
                setIsLoadingContractData(false);
            }
        };

        fetchOriginalContractData();
    }, [isOpen, lead?.originalContractId]);

    // Validación previa antes de enviar el formulario
    const validateLeadData = (lead: Lead): string[] => {
        const errors: string[] = [];

        // Validar que el lead existe
        if (!lead || !lead.id) {
            errors.push('Lead no válido o no encontrado');
            return errors;
        }

        // Verificar que no esté en estado satisfactorio o no satisfactorio usando getLeadStatesList
        const leadStates = getLeadStatesList();
        const currentState = leadStates.find(state => state.value === lead.currentStatusId);
        if (currentState && (currentState.satisfactoryStatus || currentState.unsatisfactoryStatus)) {
            errors.push('El lead no puede ser convertido desde su estado actual');
        }

        return errors;
    };

    // Función para pre-poblar los datos del contrato con información del lead y contrato original
    const getInitialContractValues = React.useCallback(() => {
        if (!lead) return {};

        // Si hay datos del contrato original, los mezclamos con los datos del lead
        if (originalContractData) {
            return {
                leadId: lead.id, // Campo crucial para vincular el contrato con el lead

                // Datos del cliente del contrato original (solo si no están en el lead)
                clientActivity: originalContractData.clientActivity || '',
                clientAddress: originalContractData.clientAddress || '',
                clientCifNif: originalContractData.clientCifNif || '',
                clientCity: originalContractData.clientCity || '',
                clientCnae: originalContractData.clientCnae || '',
                clientCompanyCreationDate: originalContractData.clientCompanyCreationDate || '',
                clientCompanyName: originalContractData.clientCompanyName || '',
                clientEmail: originalContractData.clientEmail || '',
                clientIban: originalContractData.clientIban || '',
                clientPostalCode: originalContractData.clientPostalCode || '',
                clientProvince: originalContractData.clientProvince || '',
                clientSocialSecurityNumber: originalContractData.clientSocialSecurityNumber || '',
                clientTelephone: originalContractData.clientTelephone || '',
                clientTelephoneSecondary: originalContractData.clientTelephoneSecondary || '',
                clientTelephoneThird: originalContractData.clientTelephoneThird || '',
                clientWorkersNumber: originalContractData.clientWorkersNumber || 1,
                clientMobile: originalContractData.clientMobile || '',
                clientBic: originalContractData.clientBic || '',

                // Datos del producto del contrato original
                productId: originalContractData.product?.id || '',
                complements: originalContractData.complements?.map((c: any) => ({
                    complementId: c.complements?.id,
                    amount: c.amount
                })) || [],
                productAmount: originalContractData.productAmount || 0,
                totalAmount: originalContractData.totalAmount || 0,

                // Datos del contrato original
                campaignId: originalContractData.campaign?.id || '',
                organisingCorporationId: originalContractData.organisingCorporation?.id || '',
                providingCorporationId: originalContractData.providingCorporation?.id || '',
                groupings: originalContractData.groupings || '',
                operatorName: originalContractData.operatorName || '',
                tripartiteGrouping: originalContractData.tripartiteGrouping || '',
                userLogin: originalContractData.userLogin || '',
                tutorComment: originalContractData.tutorComment || '',
                saleComment: originalContractData.saleComment || '',
                socialInsuranceAmount: originalContractData.socialInsuranceAmount || 0,
                paymentMethodsId: originalContractData.paymentMethods?.id || '',
                paymentInstalmentsNumber: originalContractData.paymentInstalmentsNumber || 1,
                expirationTypeId: originalContractData.expirationType?.id || '',
                contractType: originalContractData.contractType || null,

                // Datos del representante legal del contrato original
                legalRepresentativeEmail: originalContractData.legalRepresentativeEmail || '',
                legalRepresentativeName: originalContractData.legalRepresentativeName || '',
                legalRepresentativeNif: originalContractData.legalRepresentativeNif || '',
                legalRepresentativePosition: originalContractData.legalRepresentativePosition || '',
                legalRepresentativeSchedule: originalContractData.legalRepresentativeSchedule || '',

                // Datos del estudiante del contrato original
                studentId: originalContractData.student?.id || '',
                studentStudyId: originalContractData.studentStudy?.id || '',
                studentQuotationGroupId: originalContractData.studentQuotationGroup?.id || '',
                studentProfessionalCategoryId: originalContractData.studentProfessionalCategory?.id || '',
                studentName: originalContractData.studentName || '',
                studentFirstName: originalContractData.studentFirstName || '',
                studentLastName: originalContractData.studentLastName || '',
                studentNif: originalContractData.studentNif || '',
                studentGender: originalContractData.studentGender || '',
                studentTerrorismVictim: originalContractData.studentTerrorismVictim || false,
                studentGenderViolenceVictim: originalContractData.studentGenderViolenceVictim || false,
                studentDisability: originalContractData.studentDisability || false,
                studentSocialSecurityNumber: originalContractData.studentSocialSecurityNumber || '',
                studentFunctionalArea: originalContractData.studentFunctionalArea || '',
                studentEmail: originalContractData.studentEmail || '',
                studentTelephone: originalContractData.studentTelephone || '',
                studentTelephoneSecondary: originalContractData.studentTelephoneSecondary || '',
                studentAlternativeShippingAddress: originalContractData.studentAlternativeShippingAddress || '',
                studentContactSchedule: originalContractData.studentContactSchedule || '',
                studentBirthDate: originalContractData.studentBirthDate || '',
                studentMobile: originalContractData.studentMobile || '',
                studentComment: originalContractData.studentComment || '',

                // Datos de la consultoría del contrato original
                consultancyId: originalContractData.consultancy?.id || '',
                consultancyName: originalContractData.consultancyName || '',
                consultancyEmail: originalContractData.consultancyEmail || '',
                consultancyCif: originalContractData.consultancyCif || '',
                consultancyContact: originalContractData.consultancyContact || '',
                consultancyTelephone: originalContractData.consultancyTelephone || '',
                consultancyTelephoneSecondary: originalContractData.consultancyTelephoneSecondary || '',
                consultancyDescription: originalContractData.consultancyDescription || '',
            };
        }

        return lead;
    }, [lead, originalContractData]);

    // Función para crear un contrato desde el lead
    const handleSubmitContract = async (values: any) => {
        await handleContractSubmit(values, false);
    };

    // Función para crear un borrador desde el lead
    const handleSubmitDraft = async (values: any) => {
        await handleContractSubmit(values, true);
    };

    const handleContractSubmit = async (values: any, isDraft: boolean = false) => {
        // Validación previa
        if (!lead) {
            toast.error('No se ha seleccionado ningún lead para convertir');
            return;
        }

        const validationErrors = validateLeadData(lead);
        if (validationErrors.length > 0) {
            setValidationErrors(validationErrors);
            validationErrors.forEach(error => toast.error(error));
            return;
        }

        setIsLoading(true);
        setValidationErrors([]);

        try {
            const contractService = new ContractService();

            // Preparar los datos del contrato incluyendo el leadId
            const contractData = {
                ...values,
                leadId: lead!.id, // Asegurar que el leadId esté incluido
                origin: 'lead_conversion',
                convertedFromLead: true,
                // Incluir información adicional del lead para trazabilidad
                originalLeadData: {
                    leadId: lead!.id,
                    leadStatus: lead!.status,
                    leadCreatedAt: lead!.createdAt,
                    leadTargetDate: lead!.targetDate,
                }
            };

            let response;
            const contractType = isDraft ? 'borrador' : 'contrato';

            // Mostrar toast de inicio del proceso
            toast.loading(`Creando ${contractType} desde el lead...`, { id: 'conversion-process' });

            if (isDraft) {
                response = await contractService.createDraft(contractData);
            } else {
                response = await contractService.createCompleteContract(contractData);
            }

            const result = await response.getResponseData();

            // Dismiss loading toast
            toast.dismiss('conversion-process');

            if (result?.success) {
                //toast.success(`${contractType.charAt(0).toUpperCase() + contractType.slice(1)} creado exitosamente desde el lead`);
                // Llamar al callback de éxito si se proporciona
                onSuccess && onSuccess();
                // Cerrar el modal
                onClose();
            } else {
                handleErrors(result);
            }
        } catch (error: any) {
            // Dismiss loading toast in case of error
            toast.dismiss('conversion-process');

            console.error('Error al convertir lead a contrato:', error);

            // Manejar diferentes tipos de errores
            if (error.response) {
                // Error de respuesta HTTP
                const status = error.response.status;
                const message = error.response.data?.message || error.message;

                if (status === 400) {
                    toast.error(`Error de validación: ${message}`);
                } else if (status === 401) {
                    toast.error('No tienes permisos para realizar esta acción');
                } else if (status === 404) {
                    toast.error('El lead o los datos requeridos no fueron encontrados');
                } else if (status >= 500) {
                    toast.error('Error interno del servidor. Por favor, contacta al administrador');
                } else {
                    toast.error(`Error del servidor (${status}): ${message}`);
                }
            } else if (error.request) {
                // Error de red
                toast.error('Error de conexión. Verifica tu conexión a internet e inténtalo de nuevo');
            } else {
                // Error general
                toast.error(error.message || 'Error inesperado al convertir el lead a contrato');
            }

            // También usar el hook de manejo de errores para consistencia
            handleErrors(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (!isLoading) {
            setValidationErrors([]);
            onClose();
        } else {
            toast.warning('Por favor, espera a que termine el proceso de conversión');
        }
    };

    // Validar que el lead existe y es válido
    if (!lead) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            size="full"
            className="z-[9999]"
        >
            <div className="flex flex-col h-full">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                            Convertir Lead a Contrato
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Lead: {lead.clientCompanyName} - {lead.productTypeName}
                        </p>
                        {lead.targetDate && (
                            <p className="text-xs text-gray-500 mt-1">
                                Fecha objetivo: {new Date(lead.targetDate).toLocaleDateString('es-ES')}
                            </p>
                        )}
                    </div>
                    <div className='flex items-center space-x-4'>
                        {showClientAsideMenuBtn && (
                            <div className='flex flex-row justify-end'>
                                <Button onClick={() => setIsOpenClientHistory(!isOpenClientHistory)} className='cursor-pointer'>
                                    <FaCircleInfo /> &nbsp; {isOpenClientHistory ? "Cerrar Historial" : "Abrir Historial"}
                                </Button>
                            </div>
                        )}
                        <button
                            onClick={handleClose}
                            disabled={isLoading}
                            className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            title={isLoading ? 'Conversión en proceso...' : 'Cerrar modal'}
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Mostrar errores de validación si existen */}
                {validationErrors.length > 0 && (
                    <div className="p-4 bg-red-50 border-b border-red-200">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">
                                    Errores de validación
                                </h3>
                                <div className="mt-2 text-sm text-red-700">
                                    <ul className="list-disc pl-5 space-y-1">
                                        {validationErrors.map((error, index) => (
                                            <li key={index}>{error}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Indicador de carga para datos del contrato original */}
                {isLoadingContractData && (
                    <div className="p-4 bg-amber-50 border-b border-amber-200">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-amber-600"></div>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm font-medium text-amber-800">
                                    Cargando datos del contrato original...
                                </p>
                                <p className="text-xs text-amber-600 mt-1">
                                    Preparando información para pre-llenar el formulario
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Indicador de carga para conversión */}
                {isLoading && (
                    <div className="p-4 bg-blue-50 border-b border-blue-200">
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                            </div>
                            <div className="ml-3">
                                <p className="text-sm font-medium text-blue-800">
                                    Procesando conversión del lead...
                                </p>
                                <p className="text-xs text-blue-600 mt-1">
                                    Por favor, no cierres esta ventana
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex-1 overflow-auto px-5">
                    <ContractForm
                        submitContract={handleSubmitContract}
                        submitDraft={handleSubmitDraft}
                        data={
                            originalContractData
                                ? {
                                    ...originalContractData,
                                    ...getInitialContractValues(),
                                    id: '', // Set empty ID so it creates a new contract
                                } as unknown as Contract
                                : undefined
                        }
                    />
                </div>
            </div>
        </Modal>
    );
};

export default ConvertToContractModal;